import { FC, useState } from "react";
import { ScrollView, StyleSheet, TextInput, View } from "react-native";
import { useSelector } from "react-redux";

import { TagsInput } from "src/components";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdStack, PdText } from "src/components/layout";
import { AlertModal, tAlertModalProps } from "src/components/Modals/AlertModal";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { kEmailPattern } from "src/components/UserApplicationInfo";
import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { useAppDispatch } from "src/redux";
import { brandInviteRetailers, repInviteRetailers } from "src/redux/connectionManager/actions";
import { tModalComponentProps } from "src/redux/modals/types";
import { getApiError } from "src/util/apiNew";

type tModalParams = {
  companyId: string;
  source: "brand" | "rep";
};

const DEFAULT_BRAND_NOTE_MESSAGE = `Hi there,

Our brand is now on The Main Tab for wholesale and we'd love to invite you to come shop us here!

The Main Tab:
• offers Retailers NET 60 terms and a credit line of up $50,000
• is the only marketplace that is Sales Rep friendly
• has a strictly curated assortment of the finest brands

If you apply through this link, any orders you place for our brand on The Main Tab will not incur any commission fees for us!

Looking forward to connecting with you on this platform!

Kind regards,

(name)`;

const DEFAULT_REP_NOTE_MESSAGE = `Hi there,

I've started using The Main Tab, a luxury wholesale marketplace connecting the best brands, retailers, and sales reps! I'd like to invite you to join as an authorized retailer so we can work together on the platform.

On The Main Tab you can apply for NET 60 terms and a credit line of up to $50,000, connect with and discover new brands for your store, receive suggested interactive orders from me and your brands, and streamline your wholesale business all in one main place.

Looking forward to working with you on here!

Kind regards,

(name)`;

const InviteRetailersModal: FC<tModalComponentProps<tModalParams>> = ({ onClose, onResolve, companyId, source }) => {
  const rIsMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rCompany = useSelector(({ CompanyReducer }) =>
    CompanyReducer.type === "brand" ? CompanyReducer : { type: "retailer", id: "", name: "", vendorId: undefined },
  );
  const alertModal = useModal<tAlertModalProps>(AlertModal);
  const [sEmails, setEmails] = useState<Array<string>>([]);
  const [sNote, setNote] = useState<string>(
    (source === "brand" ? DEFAULT_BRAND_NOTE_MESSAGE : DEFAULT_REP_NOTE_MESSAGE).replace("(name)", rCompany.name),
  );
  const [sIsValid, setIsValid] = useState<boolean>(false);
  const [isSending, setSending] = useState(false);

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSend = async () => {
    setSending(true);

    try {
      let response: { error: string } | undefined;
      if (source === "brand") {
        response = await dispatch(brandInviteRetailers(companyId, { emails: sEmails, note: sNote }));
      } else {
        response = await dispatch(repInviteRetailers(companyId, { emails: sEmails, note: sNote }));
      }

      if (response?.error) {
        setSending(false);
        alertModal.openAsync({ title: "Error", description: response.error });
      } else {
        onResolve();
      }
    } catch (e) {
      setSending(false);
      const error = getApiError(e);
      dispatch({ type: "RIBBON", message: new Error(error.message) });
    }
  };

  return (
    <PdModal closeOnOverlayClick={!isSending} onClose={handleOnClose}>
      <PdModalCloseButton isDisabled={isSending} onPress={handleOnClose} />
      <ScrollView style={styles.modal}>
        <PdModalTitle style={styles.mb4}>
          {isSending ? "Inviting your Retailers..." : "Invite your Retailers"}
        </PdModalTitle>
        {isSending ? (
          <PdText>
            Please do not close this pop up; the pop up will automatically close when the e-mails have been successfully
            sent.
          </PdText>
        ) : (
          <>
            <PdText>
              Enter email addresses for every retailer you would like to invite. Separate each email address with a
              comma. Each retailer will receive an individual email invitation from you to join The Main Tab and shop
              your brand. Once they sign up, any orders they place for your brand will be commission free!
            </PdText>
            <PdText style={styles.mt2}>Example: (name@email.com, store@email.com)</PdText>
            <View style={styles.subtitle}>
              <PdText style={styles.bold}>NOTE</PdText>
              <PdText>: Can only send to 100 e-mail addresses at a time.</PdText>
            </View>
            <View style={styles.emailFormControl}>
              <TagsInput
                onChangeTags={setEmails}
                inputStyleNoTags={styles.inputNoTags}
                inputStyleWithTags={styles.inputTags}
                placeholder="Email"
                validationPattern={kEmailPattern}
                onValidityStateChange={setIsValid}
                limitTo={300}
              />
            </View>
            <View style={styles.noteFormControl}>
              <TextInput
                style={styles.input}
                multiline={true}
                numberOfLines={8}
                value={sNote}
                onChangeText={setNote}
                placeholder="Note"
              />
            </View>
          </>
        )}
        <PdStack
          style={styles.mt6}
          direction={rIsMobile ? "vertical" : "horizontal"}
          layout={[1, 1]}
          gap={3}
          isReversed={rIsMobile}
        >
          <PdButton colorScheme="purple" isDisabled={isSending} onPress={handleOnClose} variant="outline">
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton isDisabled={!sIsValid || isSending} onPress={handleOnSend}>
            <PdButtonLabel>Send</PdButtonLabel>
          </PdButton>
        </PdStack>
      </ScrollView>
    </PdModal>
  );
};

const styles = StyleSheet.create({
  bold: {
    fontWeight: "500",
  },
  emailFormControl: {
    alignItems: "center",
    flexDirection: "row",
    marginTop: 3 * kStyleConst.kSpacing,
  },
  input: {
    ...kFonts.poppinsRegularMedium,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    color: kColors.gray500,
    flex: 1,
    letterSpacing: 1,
    outlineWidth: 0,
    padding: kStyleConst.kSpacing,
  },
  inputNoTags: {
    ...kFonts.poppinsRegularMedium,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    flex: 1,
    letterSpacing: 1,
    outlineWidth: 0,
    padding: kStyleConst.kSpacing,
  },
  inputTags: {
    ...kFonts.poppinsRegularMedium,
    borderColor: kColors.lightMed,
    borderWidth: 1,
    flex: 1,
    letterSpacing: 1,
    outlineWidth: 0,
    paddingHorizontal: kStyleConst.kSpacing,
    paddingVertical: 2,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  modal: {
    maxWidth: 600,
    padding: 8 * kStyleConst.kSpacing,
    width: "100%",
  },
  mt2: {
    marginTop: 2 * kStyleConst.kSpacing,
  },
  mt6: {
    marginTop: 6 * kStyleConst.kSpacing,
  },
  noteFormControl: {
    alignItems: "flex-start",
    flexDirection: "row",
    marginTop: 3 * kStyleConst.kSpacing,
  },
  subtitle: {
    alignItems: "center",
    flexDirection: "row",
    marginTop: 2 * kStyleConst.kSpacing,
  },
});

const useInviteRetailersModal = (): tUseModal<tModalParams> => {
  return useModal<tModalParams>(InviteRetailersModal);
};

export default useInviteRetailersModal;
